import { setLabel } from '../ui/i18n';
import type { Pose } from '../controls/types';
import { createElement, setText } from '../ui/dom';
import { createIcon } from '../ui/icons';
import { createSoundscape } from './soundscape';
import type { AudioActivity } from './soundscape';
import type { Layout } from '../data/types';
import type { Object3D } from 'three';
import type { CityTimeSnapshot } from '../time/cityTime';
import { createAcousticScene } from './acousticScene';
import { createActorSounds } from './actorSounds';

export function createAmbientAudio(layout: Layout, root: Object3D, enabled = true) {
  return new AmbientAudio(createAcousticScene(layout), createActorSounds(root), enabled);
}

class AmbientAudio {
  private readonly button = createElement('button', 'sound-toggle');
  private context: AudioContext | null = null;
  private sound: ReturnType<typeof createSoundscape> | null = null;
  private muted = false;
  private disposed = false;
  private starting = false;

  constructor(
    private readonly sources: ReturnType<typeof createAcousticScene>,
    private readonly actors: ReturnType<typeof createActorSounds>,
    private readonly enabled: boolean
  ) {
    this.button.type = 'button';
    this.button.disabled = !enabled;
    this.button.addEventListener('click', () => {
      if (!this.context || this.context.state !== 'running' || this.muted) void this.enable();
      else {
        this.muted = true;
        this.sound?.setMuted(true);
        this.display();
      }
    });
    this.display();
  }

  private display(): void {
    const active = this.context?.state === 'running' && !this.muted;
    const label = active ? 'Mute sound' : 'Enable sound';
    setLabel(this.button, 'aria-label', label);
    setLabel(this.button, 'title', label);
    this.button.setAttribute('aria-pressed', String(active));
    this.button.replaceChildren(createIcon(active ? 'sound' : 'muted', 18));
    setText(createElement('span', '', this.button), label);
  }

  private async enable(): Promise<void> {
    if (!this.enabled || this.disposed || this.starting) return;
    this.starting = true;
    try {
      this.context ??= new AudioContext();
      await this.context.resume();
      if (this.disposed) return;
      this.sound ??= createSoundscape(this.context, this.sources, this.actors);
      this.muted = false;
      this.sound.setMuted(false);
    } catch {
      // A denied autoplay request leaves the explicit Enable sound control available.
    } finally {
      this.starting = false;
      this.display();
    }
  }

  private readonly gesture = (event: Event): void => {
    if (event.target instanceof Node && this.button.contains(event.target)) return;
    if (!this.context && !this.muted) void this.enable();
  };

  private readonly visibility = (): void => {
    if (!this.context) return;
    if (document.hidden)
      void this.context
        .suspend()
        .then(() => {
          this.display();
        })
        .catch(() => {
          this.display();
        });
    else if (!this.muted)
      void this.context
        .resume()
        .then(() => {
          this.display();
        })
        .catch(() => {
          this.display();
        });
  };

  mount(parent: HTMLElement): void {
    parent.append(this.button);
    /* Automation must not acquire the output device: a zero gain still interrupts Bluetooth
       audio when the browser opens an AudioContext. */
    if (!this.enabled) return;
    document.addEventListener('pointerdown', this.gesture);
    document.addEventListener('keydown', this.gesture);
    document.addEventListener('visibilitychange', this.visibility);
    if (navigator.userActivation.isActive) void this.enable();
  }

  update(pose: Pose, mode: AudioActivity, time: CityTimeSnapshot, activity: number): void {
    if (!this.muted) this.sound?.update(pose.position, pose.yaw, mode, time, activity);
  }

  dispose(): void {
    this.disposed = true;
    document.removeEventListener('pointerdown', this.gesture);
    document.removeEventListener('keydown', this.gesture);
    document.removeEventListener('visibilitychange', this.visibility);
    this.sound?.dispose();
    if (this.context) void this.context.close().catch(() => undefined);
    this.button.remove();
  }
}
